import { useEffect, useRef } from "react";
import { saveErasure } from "../lib/db";
import type { ErasureDocument } from "../state/reducer";

export const SAVE_DEBOUNCE_MS = 400;

/**
 * Persists the open erasure, debounced.
 *
 * Every marked word produces a new document, and a drag produces dozens per
 * second; writing each one straight through meant an IndexedDB transaction per
 * word. Debouncing coalesces a stroke into a single write, and the pending write
 * is flushed on unmount and on `pagehide` so nothing is lost if the reader
 * closes the tab or the mobile browser backgrounds the app mid-stroke.
 */
export const usePersistence = (
  document: ErasureDocument | null,
  onError?: (error: unknown) => void,
): void => {
  const pendingRef = useRef<ErasureDocument | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    const flush = () => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      saveErasure(pending).catch((error) => onErrorRef.current?.(error));
    };

    // `pagehide` fires on mobile Safari, where `beforeunload` does not.
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      clearTimeout(timerRef.current);
      flush();
    };
  }, []);

  useEffect(() => {
    if (!document) return;
    pendingRef.current = document;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      saveErasure(pending).catch((error) => onErrorRef.current?.(error));
    }, SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [document]);
};
